import { createAsyncThunk, createSelector, createSlice, nanoid } from "@reduxjs/toolkit";
import {
  clearCart as clearCartApi,
  fetchCart as fetchCartApi,
  refreshCartQuotes as refreshCartQuotesApi,
  removeCartItem as removeCartItemApi,
  updateCartItem as updateCartItemApi,
  upsertCartItem as upsertCartItemApi,
} from "../api/cartApi";
import {
  buildAffiliateAttributionKeyPart,
  extractAffiliateAttributionFromItem,
  getEffectiveAffiliateAttribution,
  mergeAffiliateAttributionIntoMetadata,
} from "../utils/affiliateAttribution";
import { buildPackageCartSummary } from "../utils/packageCart";
import {
  applyBookingIntentToItem,
  buildBookingIntent,
  withBookingIntent,
} from "../utils/bookingIntent";
import { buildCartBenefitsPayload } from "../utils/cartBenefitsContract";
import { cartNeedsQuoteRefresh } from "../utils/cartRequestControl";

const createInitialCheckoutState = () => ({
  activeQuoteCode: null,
  contact: {
    name: "",
    email: "",
    phone: "",
    notes: "",
  },
  payment: {
    method: "upi",
    outcome: "success",
  },
  lastBooking: null,
});

const initialState = {
  items: [],
  benefits: null,
  isOpen: false,
  checkout: createInitialCheckoutState(),
  status: "idle",
  error: null,
  lastSyncedAt: null,
  pricingError: null,
  requests: {},
  requestSequence: 0,
  acceptedSequence: 0,
  serverCartId: "",
  serverRevision: null,
};

const clampQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return Math.max(1, Math.round(parsed));
};

const buildGuestMixKey = (guestMix) => {
  if (!guestMix) return "guest:none";
  const adult = Number(guestMix.adult ?? guestMix.adults ?? guestMix.adultCount ?? 0);
  const child = Number(guestMix.child ?? guestMix.children ?? guestMix.childCount ?? 0);
  const infant = Number(guestMix.infant ?? guestMix.infants ?? guestMix.infantCount ?? 0);
  return `guest:A${Number.isFinite(adult) ? adult : 0}C${Number.isFinite(child) ? child : 0}I${Number.isFinite(infant) ? infant : 0}`;
};

const normalizeGuestMix = (guestMix) => {
  if (!guestMix || typeof guestMix !== "object") return null;
  const adult = Number(guestMix.adult ?? guestMix.adults ?? guestMix.adultCount ?? 0);
  const child = Number(guestMix.child ?? guestMix.children ?? guestMix.childCount ?? 0);
  const infant = Number(guestMix.infant ?? guestMix.infants ?? guestMix.infantCount ?? 0);
  const normalized = {
    adult: Number.isFinite(adult) ? Math.max(0, Math.floor(adult)) : 0,
    child: Number.isFinite(child) ? Math.max(0, Math.floor(child)) : 0,
    infant: Number.isFinite(infant) ? Math.max(0, Math.floor(infant)) : 0,
  };
  return {
    ...guestMix,
    ...normalized,
    adults: normalized.adult,
    children: normalized.child,
    infants: normalized.infant,
  };
};

const normalizeCouponCode = (value) => String(value || "").trim().toUpperCase();

const normalizeGroupIdList = (value = []) => {
  const raw = Array.isArray(value)
    ? value
    : String(value || "").includes(",")
      ? String(value || "").split(",")
      : [value];
  const seen = new Set();
  return raw
    .map((entry) => String(entry?.groupId || entry?.id || entry?.unitId || entry?.key || entry || "").trim())
    .filter(Boolean)
    .filter((entry) => {
      const key = entry.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const normalizeConfigList = (items = []) =>
  (items || [])
    .map((item) => ({
      id: item?.id || item?.key || item?.label || "",
      quantity: Math.max(1, Number(item?.quantity || item?.qty || item?.count || 1)),
    }))
    .filter((item) => item.id)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((item) => `${item.id}:${item.quantity}`)
    .join(",");

const buildKey = (item = {}) => {
  const referralContext = getEffectiveAffiliateAttribution(extractAffiliateAttributionFromItem(item));
  const referralKey = buildAffiliateAttributionKeyPart(referralContext);
  const experienceType = item.experienceType || "safari";
  if (experienceType === "room") {
    const checkIn =
      item.metadata?.checkIn || item.checkIn || item.travelDate || item.date || item.options?.checkIn || "";
    const checkOut =
      item.metadata?.checkOut || item.checkOut || item.options?.checkOut || "";
    const ratePlanId =
      item.metadata?.ratePlanId || item.ratePlan?.id || item.options?.ratePlan?.id || "";
    const addOnKey = normalizeConfigList(item.addOns || item.options?.addOns);
    const chargeKey = normalizeConfigList(item.selectedCharges || item.options?.selectedCharges);
    const couponCode = normalizeCouponCode(
      referralContext?.couponCode || item.couponCode || item.metadata?.couponCode,
    );
    const couponKey = couponCode ? `coupon:${couponCode}` : "";
    return [
      experienceType,
      item.identifier || item.id || "unknown",
      checkIn,
      checkOut,
      ratePlanId || "rate",
      `addons:${addOnKey || "none"}`,
      `charges:${chargeKey || "none"}`,
      ...(referralKey ? [referralKey] : []),
      ...(couponKey ? [couponKey] : []),
      buildGuestMixKey(item.guestMix || item.options?.guestMix),
    ]
      .map((part) => String(part || "").trim())
      .join("#");
  }
  const slotId = item.slot?.id || item.slot?.key || item.slot?._id || item.slot?.label || item.slot;
  const vehicleId = item.vehicle?.id || item.vehicle?.key || item.vehicle?._id || item.vehicle?.label || item.vehicle;
  const date = item.travelDate || item.date || "";
  const addOnKey = normalizeConfigList(item.addOns || item.options?.addOns);
  const chargeKey = normalizeConfigList(item.selectedCharges || item.options?.selectedCharges);
  const couponCode = normalizeCouponCode(
    referralContext?.couponCode || item.couponCode || item.metadata?.couponCode,
  );
  const couponKey = couponCode ? `coupon:${couponCode}` : "";
  const groupSelection = item.groupSelection || item.metadata?.groupSelection || item.options?.groupSelection || null;
  const selectedGroupIds = normalizeGroupIdList(groupSelection?.selectedGroupIds || groupSelection?.groupIds || groupSelection?.selectedGroupId);
  const groupKey = experienceType === "trek"
    ? [
        `groups:${selectedGroupIds.length ? selectedGroupIds.join(",") : groupSelection?.selectedGroupId || "auto"}`,
        `mode:${groupSelection?.allocationMode || (selectedGroupIds.length ? "selected" : "auto")}`,
        groupSelection?.requestedGuests ? `guests:${groupSelection.requestedGuests}` : "",
      ]
        .filter(Boolean)
        .join("|")
    : "";
  return [
    experienceType,
    item.identifier || item.id || "unknown",
    date,
    slotId || "slot",
    vehicleId || "vehicle",
    item.entryPoint || item.options?.entryPoint || "",
    ...(groupKey ? [groupKey] : []),
    `addons:${addOnKey || "none"}`,
    `charges:${chargeKey || "none"}`,
    ...(referralKey ? [referralKey] : []),
    ...(couponKey ? [couponKey] : []),
    buildGuestMixKey(item.guestMix || item.options?.guestMix),
  ]
    .map((part) => String(part || "").trim())
    .join("#");
};

const kindFromType = (experienceType = "") => {
  if (experienceType === "package") return "Package";
  if (experienceType === "trek") return "Trek";
  if (experienceType === "cruise") return "Cruise";
  return "Safari";
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const refreshPendingServerTotals = async (cart = {}, force = false) => {
  if (!force && !cartNeedsQuoteRefresh(cart?.items || [])) return cart;
  try {
    const response = await refreshCartQuotesApi({
      force,
      ...buildCartBenefitsPayload(cart?.benefits?.request || {}),
    });
    return response?.cart || response || cart;
  } catch (error) {
    // Atomic refresh conflicts include the latest persisted cart. Prefer that
    // snapshot; rolled-back refreshes include the unchanged source cart.
    const retainedCart = error?.data?.cart || cart;
    return {
      ...retainedCart,
      pricingError: {
        code: error?.data?.code || error?.code || "CART_PRICING_FAILED",
        message: error?.data?.failures?.[0]?.message || error?.message || "Could not check the latest price. Please retry.",
      },
    };
  }
};

const mapServerItemToCartItem = (item = {}) => {
  const source = typeof item?.toObject === "function" ? item.toObject() : item || {};
  const raw = applyBookingIntentToItem(source);
  const experienceType = raw.experienceType || raw.type || "safari";
  const guestMix = normalizeGuestMix(raw.guestMix || raw.options?.guestMix);
  const referralContext = extractAffiliateAttributionFromItem(raw);
  const checkIn = raw.metadata?.checkIn || raw.checkIn || raw.travelDate || raw.date || raw.options?.checkIn || raw.options?.date || null;
  const checkOut = raw.metadata?.checkOut || raw.checkOut || raw.options?.checkOut || null;
  const couponCode =
    referralContext?.couponCode ||
    raw.couponCode ||
    raw.metadata?.couponCode ||
    raw.metadata?.coupon?.publicCode ||
    raw.options?.couponCode ||
    "";
  const key = raw.key || raw.metadata?.key || buildKey(raw);
  const hasAuthoritativePrice = Boolean(
    raw?.serverTotals?.authoritative === true && raw?.serverTotals?.status === "verified",
  );
  const serverAmountPaise = toNumber(raw?.serverTotals?.amountPaise);
  const price = hasAuthoritativePrice && serverAmountPaise !== null && serverAmountPaise >= 0
    ? serverAmountPaise / 100
    : 0;
  const quoteExpiresAt = raw?.quoteMeta?.expiresAt || raw?.quote?.expiresAt || null;
  const isStale = Boolean(raw?.stale) || (quoteExpiresAt ? new Date(quoteExpiresAt).getTime() < Date.now() : false);

  return {
    ...raw,
    uid: raw._id?.toString?.() || raw.uid || key || `${raw.identifier || "cart"}-${raw.travelDate || Date.now()}`,
    serverId: raw._id?.toString?.() || raw.serverId || null,
    key: key || raw._id?.toString?.() || raw.uid,
    id: raw.identifier || raw.id,
    experienceType,
    kind: raw.kind || kindFromType(experienceType),
    tag: raw.tag || kindFromType(experienceType),
    title: raw.title || raw.vehicle?.label || raw.vehicle?.name || raw.identifier || "Experience",
    subtitle: raw.subtitle || raw.destination?.title || raw.entryPoint || "",
    quantity: clampQuantity(raw.quantity),
    price,
    priceStatus: hasAuthoritativePrice ? "verified" : "pending-quote",
    travelDate: checkIn,
    checkIn,
    checkOut,
    guestMix,
    groupSelection: raw.groupSelection || raw.metadata?.groupSelection || raw.options?.groupSelection || null,
    groupAllocations: raw.groupAllocations || raw.metadata?.groupAllocations || raw.options?.groupAllocations || [],
    couponCode,
    affiliateCode: referralContext?.affiliateCode || "",
    campaignCode: referralContext?.campaignCode || "",
    linkCode: referralContext?.linkCode || "",
    referralContext,
    options: {
      ...(raw.options || {}),
      date: checkIn,
      checkIn,
      checkOut,
      slot: raw.slot || raw.options?.slot,
      entryPoint: raw.entryPoint || raw.options?.entryPoint,
      guestMix,
      groupSelection: raw.groupSelection || raw.metadata?.groupSelection || raw.options?.groupSelection || null,
      groupAllocations: raw.groupAllocations || raw.metadata?.groupAllocations || raw.options?.groupAllocations || [],
      requiredCharges: raw.requiredCharges || raw.options?.requiredCharges,
      notes: raw.notes || raw.options?.notes,
      couponCode,
      referralContext,
    },
    quote: raw.quote || (raw.quoteCode ? { quoteCode: raw.quoteCode } : undefined),
    quoteCode: raw.quoteCode || raw.quote?.quoteCode || undefined,
    requiredCharges: raw.requiredCharges || [],
    policiesSnapshot: raw.policiesSnapshot || null,
    quoteMeta: raw.quoteMeta || null,
    metadata: mergeAffiliateAttributionIntoMetadata(raw.metadata || {}, referralContext),
    stale: isStale,
  };
};

const applyServerCart = (state, payload = {}) => {
  const cart = Array.isArray(payload) ? { items: payload } : payload || {};
  state.items = (cart.items || []).map(mapServerItemToCartItem);
  state.benefits = cart.benefits || null;
  state.lastSyncedAt = Date.now();
  state.error = null;
  state.pricingError = cart.pricingError || null;
};

// Server revisions are authoritative; request order is the compatibility
// fallback for responses without revisions. Track concurrent work separately
// so a late read cannot resurrect a removed item or unlock an ongoing write.
const beginCartRequest = (state, action, writing) => {
  state.requests = state.requests || {};
  state.requestSequence = (state.requestSequence || 0) + 1;
  state.requests[action.meta.requestId] = { sequence: state.requestSequence, writing };
  state.status = Object.values(state.requests).some((request) => request.writing) ? "saving" : "loading";
  state.error = null;
};

const finishCartRequest = (state, action, rejected = false) => {
  const request = state.requests?.[action.meta.requestId];
  if (!request) return false;
  delete state.requests[action.meta.requestId];
  const cart = action.payload || {};
  const cartId = String(cart._id || "");
  const revision = Number.isSafeInteger(cart.__v) ? cart.__v : null;
  const comparable = revision !== null && state.serverRevision !== null && cartId === state.serverCartId;
  const accept = rejected
    ? request.sequence >= (state.acceptedSequence || 0)
    : comparable
      ? revision > state.serverRevision || (revision === state.serverRevision && request.sequence >= (state.acceptedSequence || 0))
      : request.sequence >= (state.acceptedSequence || 0);
  if (accept) {
    state.acceptedSequence = Math.max(state.acceptedSequence || 0, request.sequence);
    if (rejected) state.error = action.payload || action.error?.message || "Unable to update cart. Please retry.";
    else {
      applyServerCart(state, cart);
      state.serverCartId = cartId;
      state.serverRevision = revision;
    }
  }
  const pending = Object.values(state.requests);
  state.status = pending.some((entry) => entry.writing) ? "saving"
    : pending.length ? "loading" : state.error ? "failed" : "succeeded";
  return accept;
};

export const fetchRemoteCart = createAsyncThunk("cart/fetchRemote", async (options, { rejectWithValue }) => {
  try {
    const res = await fetchCartApi();
    const cart = res?.cart || res || {};
    if (options?.readOnly === true) return cart;
    return await refreshPendingServerTotals(cart, options?.force === true);
  } catch (error) {
    return rejectWithValue(error?.message || "Unable to load cart");
  }
});

export const upsertRemoteCartItem = createAsyncThunk("cart/upsertRemoteItem", async (item, { rejectWithValue }) => {
  try {
    const res = await upsertCartItemApi(withBookingIntent(item));
    const cart = res?.cart || res || {};
    return await refreshPendingServerTotals(cart);
  } catch (error) {
    return rejectWithValue(error?.message || "Unable to save cart item");
  }
});

export const updateRemoteCartItem = createAsyncThunk(
  "cart/updateRemoteItem",
  async ({ itemId, patch }, { getState, rejectWithValue }) => {
    try {
      const current = (getState()?.cart?.items || []).find(
        (item) => String(item.serverId || item._id || item.uid) === String(itemId),
      );
      const merged = {
        ...(current || {}),
        ...(patch || {}),
        metadata: {
          ...(current?.metadata || {}),
          ...(patch?.metadata || {}),
        },
      };
      const res = await updateCartItemApi(itemId, {
        ...(patch || {}),
        bookingIntent: buildBookingIntent(merged),
      });
      const cart = res?.cart || res || {};
      return await refreshPendingServerTotals(cart);
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to update cart item");
    }
  },
);

export const removeRemoteCartItem = createAsyncThunk("cart/removeRemoteItem", async (itemId, { rejectWithValue }) => {
  try {
    const res = await removeCartItemApi(itemId);
    const cart = res?.cart || res || {};
    return cart;
  } catch (error) {
    return rejectWithValue(error?.message || "Unable to remove cart item");
  }
});

export const clearRemoteCart = createAsyncThunk("cart/clearRemote", async (_, { rejectWithValue }) => {
  try {
    const res = await clearCartApi();
    const cart = res?.cart || res || {};
    return cart;
  } catch (error) {
    return rejectWithValue(error?.message || "Unable to clear cart");
  }
});

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: {
      reducer(state, action) {
        const { uid, quantity = 1 } = action.payload;
        const existing = state.items.find((item) => item.uid === uid);
        if (existing) {
          existing.quantity += quantity;
          existing.bookingIntent = buildBookingIntent(existing);
          return;
        }
        state.items.push(withBookingIntent({
          ...action.payload,
          quantity,
        }));
      },
      prepare(item) {
        const uid = item.uid || `${item.kind || "item"}-${item.id || nanoid()}`;
        const quantity = Math.max(1, item.quantity || 1);
        return {
          payload: {
            ...item,
            uid,
            quantity,
            experienceType: item.experienceType || item.type || "safari",
          },
        };
      },
    },
    removeItem(state, action) {
      state.items = state.items.filter((item) => item.uid !== action.payload);
      if (!state.items.length) state.benefits = null;
    },
    updateQuantity(state, action) {
      const { uid, quantity } = action.payload;
      const target = state.items.find((item) => item.uid === uid);
      if (!target) return;
      target.quantity = Math.max(1, quantity);
      target.bookingIntent = buildBookingIntent(target);
    },
    clearCart(state) {
      state.items = [];
      state.benefits = null;
      state.checkout = createInitialCheckoutState();
      state.requests = {};
      state.pricingError = null;
      state.error = null;
      state.status = "idle";
      state.serverCartId = "";
      state.serverRevision = null;
    },
    openCart(state) {
      state.isOpen = true;
    },
    closeCart(state) {
      state.isOpen = false;
    },
    toggleCart(state) {
      state.isOpen = !state.isOpen;
    },
    attachQuote(state, action) {
      const { uid, quote } = action.payload;
      const target = state.items.find((item) => item.uid === uid);
      if (!target) return;
      target.quote = quote;
      target.quoteCode = quote.quoteCode;
      target.price = quote.totals?.grandTotal ?? target.price ?? 0;
      target.priceContract = quote.priceContract || target.priceContract || null;
      state.checkout.activeQuoteCode = quote.quoteCode;
    },
    setCheckoutDraft(state, action) {
      const { contact, payment } = action.payload || {};
      if (contact) {
        state.checkout.contact = {
          ...state.checkout.contact,
          ...contact,
        };
      }
      if (payment) {
        state.checkout.payment = {
          ...state.checkout.payment,
          ...payment,
        };
      }
    },
    setLastBooking(state, action) {
      state.checkout.lastBooking = action.payload || null;
    },
  },
  extraReducers: (builder) => {
    [fetchRemoteCart, upsertRemoteCartItem, updateRemoteCartItem, removeRemoteCartItem, clearRemoteCart].forEach((operation) => {
      builder.addCase(operation.pending, (state, action) => beginCartRequest(state, action, operation !== fetchRemoteCart));
      builder.addCase(operation.fulfilled, (state, action) => {
        if (!finishCartRequest(state, action)) return;
        if (operation === upsertRemoteCartItem) state.isOpen = true;
        if (operation === clearRemoteCart) state.checkout = createInitialCheckoutState();
      });
      builder.addCase(operation.rejected, (state, action) => { finishCartRequest(state, action, true); });
    });
  },
});

export const selectCartState = (state) => state.cart || initialState;

export const selectCartItems = createSelector(selectCartState, (cart) => cart.items);
export const selectCartBenefits = createSelector(selectCartState, (cart) => cart.benefits || null);

export const selectCartSummary = createSelector(selectCartItems, (items) => buildPackageCartSummary(items));

export const selectCartOpen = createSelector(selectCartState, (cart) => cart.isOpen);

export const selectCheckoutState = createSelector(selectCartState, (cart) => cart.checkout || createInitialCheckoutState());

export const selectCartStatus = createSelector(selectCartState, (cart) => cart.status || "idle");
export const selectCartError = createSelector(selectCartState, (cart) => cart.error || null);
export const selectCartPricingError = createSelector(selectCartState, (cart) => cart.pricingError || null);
export const selectHasPendingServerTotals = createSelector(
  selectCartItems,
  (items) => (items || []).some((item) => item?.priceStatus !== "verified"),
);

export const {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  openCart,
  closeCart,
  toggleCart,
  attachQuote,
  setCheckoutDraft,
  setLastBooking,
} = cartSlice.actions;

export default cartSlice.reducer;
